/**
 * The GitHub issue-form links the Help page's report paths open.
 *
 * ONE COMPUTATION, TWO OUTPUTS. `buildReportLink` returns the href *and* the receipt chips
 * the Door B card renders beneath the button, because the handoff requires the chips to
 * show exactly what travels with the link ("Keep it in sync with whatever the
 * implementation actually sends", `design_handoff_help_faq/README.md` § Door B prefill).
 * Two call sites deriving the same list separately is precisely how a receipt ends up
 * promising something the URL does not carry, and the reporter's only assurance about what
 * leaves the app is that receipt. So the chips are a return value, never re-derived.
 *
 * NO ACCESS TO IDENTITY. Everything this module can put on a URL arrives through
 * {@link DefectDiagnostics}: an environment label, a version string, a route, and a role
 * *display label*. It never sees `MeResponse`, so there is no EID, display name, email, or
 * timesheet value within reach — R9 is enforced by the parameter list, not by review.
 */

/**
 * `issues/new` on the repository, which is where a `template=` query parameter is resolved.
 * The repo is private; the link 404s for anyone without Read, which is the correct
 * behaviour for a link that only signed-in staff can see in the first place.
 */
const ISSUE_FORM_BASE_URL = 'https://github.com/eis1-eta/csol-time-tracking/issues/new';

/**
 * Template filenames as they exist in `.github/ISSUE_TEMPLATE/`. GitHub resolves
 * `template=` against the filename, so these are a contract with that directory and must
 * be updated together with it.
 *
 * The numeric prefixes are load-bearing there — the issue chooser orders entries
 * alphanumerically — so they are part of the filename, not decoration. The prototype's
 * provisional `defect.yml` / `idea.yml` (README § Door B prefill, which calls its own URL
 * shape a placeholder) would resolve to nothing.
 */
const ISSUE_TEMPLATE_FILES = {
  defect: '01-defect.yml',
  idea: '02-idea.yml',
} as const;

/** The `template=` parameter name — GitHub's, not ours, hence not a field id below. */
const TEMPLATE_PARAM = 'template';

/**
 * The route recorded in the defect form's `page` field.
 *
 * A constant, not a router lookup. The Help page is the only place in the app that offers
 * a report link, so `useLocation()` here could only ever return `/help` — plumbing it
 * would add a dependency for a value with one possible answer, and would read as though
 * the field tracked something it does not. When a report affordance is added elsewhere,
 * this becomes a parameter of {@link DefectDiagnostics} (it already is, in shape) and the
 * caller passes its own route.
 */
export const REPORT_SOURCE_PAGE = '/help';

/**
 * The diagnostics the defect form accepts as prefill.
 *
 * Each key is a body-element `id` declared by `.github/ISSUE_TEMPLATE/01-defect.yml`, and
 * `DEFECT_FIELD_ORDER` below is what ties the two together: a prefill query-parameter name
 * must match a body element's `id` exactly, or GitHub drops the value silently. The four
 * are `input` elements in that form for the same reason — only `input` and `textarea`
 * accept URL prefill.
 */
export interface DefectDiagnostics {
  /** Deployed environment label, e.g. `Production`. */
  readonly environment: string;
  /** Display version, e.g. `v0.1.0`. */
  readonly version: string;
  /** Route the report was started from — see {@link REPORT_SOURCE_PAGE}. */
  readonly page: string;
  /**
   * Role *display label* (`Contributor` / `Approver` / `Administrator`), or `undefined`
   * while the viewer's role is not yet known.
   *
   * `undefined` omits the field from both the URL and the receipt rather than defaulting
   * it. `/v1/me` is unresolved on every first paint and can fail outright, and a defaulted
   * label would tell triage that an administrator filed as a contributor — a wrong answer
   * in a diagnostic field is worse than a blank one, which the form's own leading copy
   * already tells the reporter how to fill in by hand.
   */
  readonly role: string | undefined;
}

/**
 * Field order on the URL and in the receipt — one list, so the chips read in the order the
 * form asks for them. `satisfies` makes every entry a real {@link DefectDiagnostics} key,
 * so a typo in a field id fails to compile instead of producing a parameter GitHub ignores.
 */
const DEFECT_FIELD_ORDER = [
  'environment',
  'version',
  'page',
  'role',
] as const satisfies readonly (keyof DefectDiagnostics)[];

/**
 * Which form to open. A discriminated union rather than one parameter set, because the
 * diagnostic set is per-template: `01-defect.yml` declares the four field ids and
 * `02-idea.yml` declares none. Sending diagnostics to the idea form would put unrecognised
 * prefill ids on the URL, and GitHub documents neither outcome — community reports have it
 * silently ignoring them, or 404ing. The union makes the wrong call unrepresentable rather
 * than merely discouraged.
 */
export type ReportLinkRequest =
  | { readonly template: 'defect'; readonly diagnostics: DefectDiagnostics }
  | { readonly template: 'idea' };

/** One receipt chip: the value that travels, keyed by the field it fills. */
export interface ReportChip {
  /** Body-element id this value is prefilled into. Stable React key. */
  readonly field: string;
  /** The value itself — what the chip displays and what the URL carries. */
  readonly value: string;
}

export interface ReportLink {
  /** Absolute `issues/new` URL, ready for an `href`. */
  readonly href: string;
  /**
   * The prefilled values, in form order — empty for the idea template. Render these; do
   * not re-read the href, and do not assemble a second list.
   */
  readonly chips: readonly ReportChip[];
}

/**
 * Build the href and the receipt for one report path.
 *
 * WHAT IS DELIBERATELY ABSENT: `labels`, `assignees`, `milestone`, and `projects`. GitHub
 * gates each of those query parameters on the matching repository permission and returns
 * **404** to a user who lacks it — and every reporter this link exists for holds Read only,
 * so the handoff's `&labels=type%3Adefect%2Cneeds-triage` would have failed for all of
 * them. Labels are applied server-side by `.github/workflows/issue-triage.yml` instead.
 * That is a recorded divergence from the prototype; `report-url.test.ts` guards it.
 */
export function buildReportLink(request: ReportLinkRequest): ReportLink {
  const params = new URLSearchParams([[TEMPLATE_PARAM, ISSUE_TEMPLATE_FILES[request.template]]]);

  const chips = request.template === 'defect' ? defectChips(request.diagnostics) : [];
  for (const chip of chips) params.append(chip.field, chip.value);

  return { href: `${ISSUE_FORM_BASE_URL}?${params.toString()}`, chips };
}

/** The diagnostics that have a value, in form order. */
function defectChips(diagnostics: DefectDiagnostics): readonly ReportChip[] {
  return DEFECT_FIELD_ORDER.flatMap((field) => {
    const value = diagnostics[field];
    return value === undefined ? [] : [{ field, value }];
  });
}
